import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Credencial {
  id: string;
  workspaceId: string;
  portal: string;
  usuario: string;
  lastUsedAt: string | null;
  createdAt: string;
}

export interface SaveCredencialInput {
  portal: string;
  usuario: string;
  senha: string;
  userPassword: string;
}

export interface RetrieveCredencialInput {
  userPassword: string;
}

export interface RetrieveCredencialResult {
  senha: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function cofrePath(workspaceId: string): string {
  return `/v1/workspaces/${workspaceId}/cofre`;
}

// ─── Hooks ───────────────────────────────────────────────────────────────────

export function useCofre(workspaceId: string | null | undefined) {
  return useQuery({
    enabled: !!workspaceId,
    queryKey: ['cofre', workspaceId],
    queryFn: () => api.get<{ items: Credencial[] }>(cofrePath(workspaceId!)),
  });
}

export function useSaveCredencial(workspaceId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SaveCredencialInput) =>
      api.post<Credencial>(cofrePath(workspaceId!), input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cofre', workspaceId] });
    },
  });
}

export function useRetrieveCredencial(workspaceId: string | null | undefined) {
  return useMutation({
    mutationFn: ({ credencialId, userPassword }: { credencialId: string; userPassword: string }) =>
      api.post<RetrieveCredencialResult>(
        `${cofrePath(workspaceId!)}/${credencialId}/retrieve`,
        { userPassword } satisfies RetrieveCredencialInput,
      ),
  });
}

export function useDeleteCredencial(workspaceId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; userPassword: string }) =>
      api.delete<{ ok: boolean }>(`${cofrePath(workspaceId!)}/${vars.id}`, {
        body: JSON.stringify({ userPassword: vars.userPassword }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cofre', workspaceId] });
    },
  });
}
